import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './CostingParameterManager.css';

const CostingParameterManager = () => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingParam, setEditingParam] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    value: ''
  });

  useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/costing-parameters');
      setParameters(response.data);
    } catch (err) {
      setError('Failed to fetch costing parameters');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        value: parseFloat(formData.value)
      };

      if (editingParam) {
        await axios.put(`/api/costing-parameters/${editingParam.id}`, payload);
        setSuccess('Parameter updated successfully');
      } else {
        await axios.post('/api/costing-parameters', payload);
        setSuccess('Parameter created successfully');
      }
      resetForm();
      fetchParameters();
    } catch (err) {
      let errorMsg = 'Failed to save costing parameter';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
    }
  };

  const handleEdit = (param) => {
    setEditingParam(param);
    setFormData({
      name: param.name,
      value: param.value.toString()
    });
    setShowForm(true);
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDelete = async (id) => {
    openConfirmDialog('Delete Parameter', 'Are you sure you want to delete this parameter?', 'Delete', async () => {
      try {
        await axios.delete(`/api/costing-parameters/${id}`);
        setSuccess('Parameter deleted successfully');
        fetchParameters();
      } catch (err) {
        setError('Failed to delete parameter');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      value: ''
    });
    setEditingParam(null);
    setShowForm(false);
  };

  return (
    <div className="costing-manager">
      <div className="header-section">
        <h1>Costing Parameter Maintenance</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Add Parameter
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingParam ? 'Edit Parameter' : 'Add Parameter'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="costing-form">
                <div className="form-group">
                  <label>Parameter Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Markup, COReturn-COR"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Value (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingParam ? 'Update' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <p>Loading parameters...</p>
        ) : (
          <table className="costing-table">
            <thead>
              <tr>
                <th>Parameter Name</th>
                <th>Value (%)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {parameters.length > 0 ? (
                parameters.map((param) => (
                  <tr key={param.id}>
                    <td>{param.name}</td>
                    <td>{param.value}%</td>
                    <td>
                      <button className="btn btn-sm btn-info" onClick={() => handleEdit(param)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(param.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No costing parameters found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostingParameterManager;
